import type { FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { buttonClass } from '@/lib/ui'
import { Notice, TextArea, TextField } from '@/components/generals/form'

const TOPICS = ['Visiting', 'Accessibility', 'Groups and schools', 'Private hire', 'Research', 'Something else']
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export const ContactForm = (): ReactNode => {
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [topic, setTopic] = useState(TOPICS[0])
    const [message, setMessage] = useState('')
    const [website, setWebsite] = useState('')
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
    const [feedback, setFeedback] = useState('')

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const nextErrors: Record<string, string> = {}

        if (name.trim().length < 2) nextErrors.name = 'Please tell us your name.'
        if (!EMAIL_PATTERN.test(email.trim())) nextErrors.email = 'Please check your email address.'
        if (message.trim().length < 10) nextErrors.message = 'Please write a little more so we can help.'

        setErrors(nextErrors)

        if (Object.keys(nextErrors).length) return

        setStatus('sending')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, email, topic, message, website })
            })
            const data = await response.json().catch(() => null) as { message?: string, errors?: Record<string, string> } | null

            if (!response.ok) {
                if (data?.errors) setErrors(data.errors)
                setStatus('error')
                setFeedback(data?.message ?? 'We could not send that just now. Please try again.')
                return
            }

            setStatus('sent')
            setFeedback(data?.message ?? 'Thank you. We will reply within two working days.')
            setMessage('')
        }
        catch {
            setStatus('error')
            setFeedback('We could not send that just now. Please try again in a few minutes.')
        }
    }

    return (
        <form onSubmit={handleSubmit} noValidate className='space-y-8'>
            <div className='grid gap-8 sm:grid-cols-2'>
                <TextField id='contact-name' label='Your name' value={name} onChange={setName} autoComplete='name' required error={errors.name} />
                <TextField id='contact-email' label='Email' type='email' inputMode='email' value={email} onChange={setEmail} autoComplete='email' required error={errors.email} />
            </div>
            <div>
                <label htmlFor='contact-topic' className='label-mono text-star-3'>
                    About
                </label>
                <select id='contact-topic' value={topic} onChange={event => setTopic(event.target.value)} className='field mt-1'>
                    {TOPICS.map(item => (
                        <option key={item} value={item}>
                            {item}
                        </option>
                    ))}
                </select>
            </div>
            <TextArea id='contact-message' label='Your message' value={message} onChange={setMessage} required rows={5} maxLength={4000} error={errors.message} />
            <div aria-hidden className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                <label htmlFor='contact-website'>
                    Leave this empty
                </label>
                <input id='contact-website' tabIndex={-1} autoComplete='off' value={website} onChange={event => setWebsite(event.target.value)} />
            </div>
            {feedback && (
                <Notice tone={status === 'sent' ? 'success' : 'error'}>
                    {feedback}
                </Notice>
            )}
            <button type='submit' disabled={status === 'sending'} className={buttonClass('primary')}>
                {status === 'sending' ? 'Sending' : 'Send message'}
            </button>
        </form>
    )
}
